// leadLine — 表示専用リード線 (display-only lead line, modern-layout.md §9.8.1).
//
// 改訂 (2026-06-09 ①): 細い SVG ヒット線 → drawing-core の実ペン (色/太さ/濃さ/AA)
// ラスタライズへ格上げ。SVG 描画 (LeadLineOverlay) は no-op 化し、本モジュールは
// 「リード区間の **canvas-space 点列計算**」(computeLeadSegmentPoints) と、旧
// SVG 用の LeadLineSource publish API を引き続き提供する。実ペン描画は
// CanvasDrawingEngine.setLead(engine 経由で drawing-core) が担う。
//
// なぜ必要か (consult 2026-06-09):
//   手ぶれ補正 ON (§9.8 距離リサンプル + EMA、causal フィルタ) では、causal
//   フィルタの性質上 **ラグ = 過去サンプルの平均** で、ラグを減らすこと =
//   平滑を弱めることと coupling している。○ブラシカーソル (BrushCursorOverlay)
//   は生 pointermove を追うので正確にカーソルへ居るのに、描かれる (平滑された)
//   線はその後ろに遅れて付いてくる。これがラグ感として目立つ。
//
//   採用案 C = **補正不変・display-only**: 確定・送信するジオメトリ (平滑済み
//   点列) は 100% 不変のまま、画面に「最後の確定 (平滑) 点 → 生カーソル」を結ぶ
//   **仮の直線リード**だけを毎フレーム重畳表示する。生のガタガタ軌跡ではなく
//   直線にすることで、補正で消したジッタを先端で見せない (輪ゴム的にカーソルへ
//   向かってる感だけ出す)。stroke 終端は既存の PointFilter.flush() が収束するので、
//   C は実質「flush を stroke 中にも見せる」拡張。
//
// determinism / parity (§9.8.1):
//   - リード線は **未コミット・未送信** = stroke buffer (コミット & 送信される
//     本体) に焼かない。canonical な点列は平滑のまま → 他クライアント・サーバ
//     snapshot は平滑された線を見る。ローカルの描画者だけが仮リードを見る
//     (= ○リングと同じ表示専用ヒント)。protocol / サーバ / wire を一切触らない。
//   - このモジュールは pixel を描かない。値の publish (LeadLineSource) と
//     座標計算 (computeLeadLineGeometry) のみ。実 DOM 描画は LeadLineOverlay。
//
// 座標系 (○リングと一致させる、§9.8.1 要検証):
//   - リード**始点** = engine が emit した最新の平滑点。StrokePoint は canvas
//     native px なので、`.relative` wrapper (= native × zoom の box) に重ねるには
//     `× zoom` で wrapper-local px へ変換する。
//   - リード**終点** = 生カーソル。BrushCursorOverlay と同じく wrapper の
//     pointermove `clientX/Y` を `getBoundingClientRect` で wrapper-local px に
//     変換した値。既に zoom 適用後の視覚座標なので別途 zoom を掛けない。
//   → 始点 (anchor×zoom) と終点 (cursor) が同じ wrapper-local px 系に乗るので、
//     リード先端が○リング中心と一致する。

/**
 * リード線の始点 (= 最後の確定平滑点) を canvas native px で保持する不変スナップ。
 * `null` のときアンカー未確立 (= まだ平滑点が 1 つも出ていない)。
 */
export interface LeadLineAnchor {
  /** canvas native px. */
  readonly x: number;
  /** canvas native px. */
  readonly y: number;
  /**
   * width 軸 effective pressure (∈ 0..1) of the last emitted (committed,
   * stabilised) point. リード先端 (anchor→cursor 区間) は確定線と同じ
   * 筆圧→幅マッピングを通すため、確定線が終わった筆圧をここで引き継ぐ。
   * `undefined` なら base width (= 非筆圧デバイス / 筆圧 OFF、確定線も base)。
   *
   * 2026-06-27 fix: これが欠けていると anchor 起点の lead 区間が widthScale=1
   * (= 最大幅) で描かれ、「筆圧 ON + 手ぶれ補正 ON で未確定先端だけ最大幅」
   * になっていた (確定線は細いのに先端だけ太い)。
   */
  readonly p?: number;
  /** alpha 軸 effective pressure (∈ 0..1) of the last emitted point。 */
  readonly pAlpha?: number;
}

/**
 * LeadLineSource — DrawingCanvas (publisher) と LeadLineOverlay (subscriber) を
 * 疎結合に繋ぐ ref 流儀の小さな可変パブリッシャ。React state を介さない
 * (高頻度 pointermove / rAF 経路を再レンダリングで汚さない、○リング流儀)。
 *
 * DrawingCanvas 側:
 *   - `begin()`      … stroke 開始 (pointerdown)。anchor を空にして active 化。
 *   - `setAnchor()`  … filtered 点を emit するたび、その末尾 (最新平滑点) を publish。
 *   - `end()`        … stroke 終了 (pointerup/cancel/leave)。inactive 化 + anchor クリア。
 *
 * LeadLineOverlay 側:
 *   - `getAnchor()`  … 現在の anchor (null = リード非表示)。
 *   - `isActive()`   … stroke 中か (false = 非表示)。
 *
 * **送信不変の担保**: source は read-only な publish のみで、engine の
 * addPoints/endStroke/transport 送信経路を 1 行も変えない。リード線導入の前後で
 * 送信点列は bit-identical (§9.8.1 determinism)。
 */
export interface LeadLineSource {
  begin(): void;
  setAnchor(x: number, y: number): void;
  end(): void;
  getAnchor(): LeadLineAnchor | null;
  isActive(): boolean;
}

export const createLeadLineSource = (): LeadLineSource => {
  let anchor: LeadLineAnchor | null = null;
  let active = false;
  return {
    begin() {
      active = true;
      anchor = null;
    },
    setAnchor(x, y) {
      // 非有限値はアンカーを動かさない (NaN が transform に流れて線が壊れるのを防ぐ)。
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      anchor = { x, y };
    },
    end() {
      active = false;
      anchor = null;
    },
    getAnchor() {
      return anchor;
    },
    isActive() {
      return active;
    },
  };
};

/** リード線 1 本の幾何 (wrapper-local px 系、SVG にそのまま流せる端点)。 */
export interface LeadLineGeometry {
  /** 始点 (= 最後の平滑点) を wrapper-local px に変換したもの。 */
  readonly x1: number;
  readonly y1: number;
  /** 終点 (= 生カーソル) の wrapper-local px。 */
  readonly x2: number;
  readonly y2: number;
}

/**
 * リード線の端点を求める純関数 (テスト容易性のため component から分離、
 * BrushCursorOverlay の computeBrushCursorScreenDiameter と同流儀)。
 *
 * @param anchor 最後の確定平滑点 (canvas native px) または null。
 * @param cursor 生カーソル位置 (wrapper-local px、○リングと同じ値) または null。
 * @param zoom   現在の表示 zoom (`.relative` box = native × zoom)。
 * @param active stroke 中かつ手ぶれ補正 ON (= リードを出すべき状態) か。
 * @returns 描くべきリード線の端点。出さない場合 (no-op) は null。
 *
 * **null を返す (= リード非表示) 条件**:
 *   - `active === false`        … stroke 中でない / 手ぶれ補正 OFF (§9.8.1 no-op)。
 *   - `anchor === null`         … 平滑点未確立 (pen-down 直後)。
 *   - `cursor === null`         … カーソル位置未取得。
 *   - zoom が非有限 / 非正       … 防御。
 *   - 始点と終点がほぼ一致 (ゼロ長) … OFF 相当 / ラグ無しなら線を描かない
 *     (補正 OFF は anchor がカーソルに即追従 ⇒ ゼロ長 ⇒ ここで非表示、
 *      Classic / Modern-off で従来と完全一致)。
 */
export const LEAD_LINE_MIN_LENGTH_PX = 0.5;

export const computeLeadLineGeometry = (
  anchor: LeadLineAnchor | null,
  cursor: { readonly x: number; readonly y: number } | null,
  zoom: number,
  active: boolean,
): LeadLineGeometry | null => {
  if (!active) return null;
  if (anchor === null || cursor === null) return null;
  if (!Number.isFinite(zoom) || zoom <= 0) return null;
  if (!Number.isFinite(cursor.x) || !Number.isFinite(cursor.y)) return null;
  // 始点 = 最後の平滑点 (canvas native px) を wrapper-local px へ (× zoom)。
  const x1 = anchor.x * zoom;
  const y1 = anchor.y * zoom;
  const x2 = cursor.x;
  const y2 = cursor.y;
  const dx = x2 - x1;
  const dy = y2 - y1;
  // ゼロ長 (= OFF 相当 / ラグ無し) は描かない。screen px 基準で判定。
  if (dx * dx + dy * dy < LEAD_LINE_MIN_LENGTH_PX * LEAD_LINE_MIN_LENGTH_PX) {
    return null;
  }
  return { x1, y1, x2, y2 };
};

// ─── ① 実ペンリード = canvas-space 点列計算 (modern-layout.md §9.8.1 ①) ───────
//
// drawing-core の `setLead` に渡す **canvas native px** の点列を求める純関数。
// SVG 用の `computeLeadLineGeometry` (wrapper-local px、× zoom) とは別物で、
// こちらは zoom を掛けない (= StrokePoint と同じ canvas 座標系)。

/** リードに渡す最小の点 (canvas native px + optional 筆圧軸)。 */
export interface LeadSamplePoint {
  readonly x: number;
  readonly y: number;
  /** width 軸 effective pressure (∈ 0..1) — 未指定ならペン base width。 */
  readonly p?: number;
  /** alpha 軸 effective pressure (∈ 0..1)。 */
  readonly pAlpha?: number;
}

/**
 * リードのなぞり方:
 *   - `'raw'`     … 「確定平滑点 (anchor) → 未確定の生入力列 → カーソル」を辿る
 *                    (= 自分が今引いている生の軌跡がそのまま先端に見える、第一候補)。
 *   - `'straight'` … 「確定平滑点 (anchor) → カーソル」の直線 (= 生ジッタを先端で
 *                    見せず輪ゴム的に「カーソルへ向かう」感だけ出す fallback)。
 * 実装者が両方試せるよう flag 化 (最終判断はユーザ feel テスト、§9.8.1 ①)。
 */
export type LeadPathMode = 'raw' | 'straight';

/**
 * リード区間の canvas-space 点列を組む純関数。
 *
 * @param anchor  最後に確定 (emit) した平滑点 (canvas native px) または null。
 * @param rawTail anchor 以降にまだ平滑出力へ反映されていない生入力列 (canvas px)。
 *                emit 後にクリアせず別 ref に溜めた「確定平滑フロンティア〜カーソル」。
 * @param cursor  最新の生カーソル (canvas px) または null。
 * @param mode    'raw' (生をなぞる) / 'straight' (anchor→cursor 直線)。
 * @param minLenPx ゼロ長判定の閾値 (canvas px²で比較)。既定 `LEAD_LINE_MIN_LENGTH_PX`。
 * @returns engine.setLead に渡す点列 (先頭 = anchor)。出さない場合は `[]`。
 *
 * **空配列を返す (= リード非表示) 条件**:
 *   - anchor / cursor が null。
 *   - 非有限座標。
 *   - anchor〜cursor がほぼゼロ長 (= 補正 OFF 相当 / ラグ無し)。
 *
 * 負荷 (§9.8.1 ①): rawTail は「隙間ぶん」だけ (= 数〜十数点) を想定。胴体 (平滑線)
 * は含めない。'straight' は常に 2 点。
 */
export const computeLeadSegmentPoints = (
  anchor: LeadLineAnchor | null,
  rawTail: readonly LeadSamplePoint[],
  cursor: LeadSamplePoint | null,
  mode: LeadPathMode,
  minLenPx: number = LEAD_LINE_MIN_LENGTH_PX,
): LeadSamplePoint[] => {
  if (anchor === null || cursor === null) return [];
  if (!Number.isFinite(anchor.x) || !Number.isFinite(anchor.y)) return [];
  if (!Number.isFinite(cursor.x) || !Number.isFinite(cursor.y)) return [];
  // anchor〜cursor の直線距離でゼロ長判定 (canvas px)。生をなぞる場合も、終端が
  // anchor とほぼ一致するなら隙間が無い = 非表示。
  const dx = cursor.x - anchor.x;
  const dy = cursor.y - anchor.y;
  if (dx * dx + dy * dy < minLenPx * minLenPx) return [];

  // anchor は最後に確定 (emit) した平滑点。その筆圧 (p / pAlpha) を head に載せて
  // おくことで、リード区間 (anchor→…→cursor) が確定線と同じ筆圧→幅マッピングを
  // 通る (= 確定線の終端幅から連続する)。anchor が筆圧を持たなければ base width。
  const head: LeadSamplePoint = sanitizeSample({
    x: anchor.x,
    y: anchor.y,
    ...(anchor.p !== undefined ? { p: anchor.p } : {}),
    ...(anchor.pAlpha !== undefined ? { pAlpha: anchor.pAlpha } : {}),
  });
  if (mode === 'straight') {
    return [head, sanitizeSample(cursor)];
  }
  // 'raw': anchor → 生入力列 → cursor。非有限点は捨てる。連続重複は drawLine 側で
  // segLen=0 として無害なので特別扱いしない。
  const out: LeadSamplePoint[] = [head];
  for (const pt of rawTail) {
    if (!Number.isFinite(pt.x) || !Number.isFinite(pt.y)) continue;
    out.push(sanitizeSample(pt));
  }
  // cursor が rawTail 末尾と厳密一致でなければ最後に足す (= 最新カーソルまで届かせる)。
  const lastPushed = out[out.length - 1];
  if (lastPushed === undefined || lastPushed.x !== cursor.x || lastPushed.y !== cursor.y) {
    out.push(sanitizeSample(cursor));
  }
  return out;
};

/** optional 筆圧軸を保ったまま座標だけ取り出す (undefined field を含めない)。 */
const sanitizeSample = (pt: LeadSamplePoint): LeadSamplePoint => {
  if (pt.p !== undefined && pt.pAlpha !== undefined) {
    return { x: pt.x, y: pt.y, p: pt.p, pAlpha: pt.pAlpha };
  }
  if (pt.p !== undefined) return { x: pt.x, y: pt.y, p: pt.p };
  if (pt.pAlpha !== undefined) return { x: pt.x, y: pt.y, pAlpha: pt.pAlpha };
  return { x: pt.x, y: pt.y };
};
